/**
 * AppNav — THE unified navigation, identical on every route (redesign item 1).
 * Primary: Stories · Content ▾ · Ask · Reports · Insights
 * Utility: Search · Archive · Settings · theme toggle · Account ▾
 * Auth-aware: anonymous visitors get the same primary nav + "Join free" CTA.
 */
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Search, Sun, Moon, LogOut, Bell, BellOff, Shield } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useDarkMode } from "../hooks/useDarkMode";
import { useAdminMode } from "../hooks/useAdminMode";
import { usePushNotifications } from "../hooks/usePushNotifications";
import ArchiveModal from "./ArchiveModal";
import SettingsModal from "./SettingsModal";
import AdminRibbon from "./AdminRibbon";
import MobileTabBar from "./MobileTabBar";

const CONTENT_ITEMS = [
  { to: "/briefs", label: "Briefs", desc: "Weekly editorial brief", testid: "briefs-archive-nav-link" },
  { to: "/podcast", label: "Podcast", desc: "Audio briefing, weekly", testid: "podcast-nav-link" },
  { to: "/newsletters", label: "Newsletter", desc: "Subscribe & past issues", testid: "newsletters-nav-link" },
  { to: "/officina-turistica", label: "Officina Turistica", desc: "Italian blog, in English", testid: "officina-nav-link" },
  { to: "/events", label: "Events", desc: "Industry calendar", testid: "events-nav-link" },
  { to: "/ai-travel-statistics", label: "AI Statistics", desc: "Key numbers, monthly", testid: "ai-stats-nav-link" },
  { to: "/glossary", label: "Glossary", desc: "Industry terms, explained", testid: "glossary-nav-link" },
];

const Pill = ({ text, dm }) => (
  <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full ${
    text === "NEW"
      ? (dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-100 text-emerald-700")
      : (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]")
  }`}>{text}</span>
);

export default function AppNav({ active = "", darkMode = false, tickerVisible = false }) {
  const { user, isAdmin, logout, authHeader } = useAuth() || {};
  const [dmPref, setDmPref] = useDarkMode();
  const [adminMode, setAdminMode] = useAdminMode();
  const push = usePushNotifications(user ? authHeader : null);
  const navigate = useNavigate();
  const location = useLocation();
  const dm = darkMode;

  const [contentOpen, setContentOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const contentRef = useRef(null);
  const accountRef = useRef(null);

  useEffect(() => {
    if (!contentOpen && !accountOpen) return;
    const onDocClick = (e) => {
      if (contentRef.current && !contentRef.current.contains(e.target)) setContentOpen(false);
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [contentOpen, accountOpen]);

  const handleSearch = () => {
    if (location.pathname === "/feed") {
      window.dispatchEvent(new Event("ti-focus-search"));
    } else {
      navigate("/feed?focus=search");
    }
  };

  const linkCls = (isActive) =>
    `category-tag flex items-center gap-1.5 pb-0.5 border-b-2 transition-colors ${
      isActive
        ? (dm ? "text-cyan-400 border-cyan-400" : "text-[#006874] border-[#006874]")
        : (dm ? "text-slate-400 border-transparent hover:text-slate-200" : "text-[#6b7a7d] border-transparent hover:text-[#191c1d]")
    }`;

  const utilBtnCls = `category-tag flex items-center gap-1.5 px-1 py-0.5 transition-colors ${
    dm ? "text-slate-400 hover:text-slate-100" : "text-[#6b7a7d] hover:text-[#191c1d]"
  }`;

  return (
    <>
    <header
      data-testid="app-nav"
      className={`sticky z-50 ${tickerVisible ? "top-8" : "top-0"}`}
      style={{
        background: dm ? "rgba(12,16,17,0.88)" : "rgba(248,249,250,0.88)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: dm ? "1px solid rgba(186,201,204,0.08)" : "1px solid rgba(186,201,204,0.2)",
      }}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        {/* Logo + primary nav */}
        <div className="flex items-center gap-8 min-w-0">
          <Link to={user ? "/feed" : "/"} data-testid="app-nav-logo" className="flex-shrink-0 flex items-center">
            <img
              src={dm ? "/TI_logo_white_crop.webp" : "/TI_logo_color_crop.webp"}
              alt="TourismIntel"
              width={130}
              height={28}
              style={{ height: 28, width: 130, objectFit: "contain", maxWidth: "none" }}
            />
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/feed" data-testid="stories-nav-link" className={linkCls(active === "stories")}>
              Stories
            </Link>

            <Link to="/pulse" data-testid="pulse-nav-link" className={linkCls(active === "pulse")}>
              Pulse <Pill text="NEW" dm={dm} />
            </Link>

            {/* Content ▾ — groups the editorial products */}
            <div ref={contentRef} className="relative">
              <button
                onClick={() => setContentOpen((o) => !o)}
                data-testid="content-dropdown-btn"
                aria-expanded={contentOpen}
                className={`${linkCls(active === "content")} ${contentOpen && active !== "content" ? (dm ? "text-slate-200" : "text-[#191c1d]") : ""}`}
              >
                Content <ChevronDown className={`w-3 h-3 transition-transform ${contentOpen ? "rotate-180" : ""}`} />
              </button>
              {contentOpen && (
                <div
                  data-testid="content-dropdown-menu"
                  className={`absolute left-0 top-full mt-2 w-60 rounded-xl overflow-hidden z-50 animate-fade-in ${dm ? "bg-[#252e2f] border border-white/5" : "bg-white border border-black/5"}`}
                  style={{ boxShadow: "0 14px 32px rgba(16,40,44,0.14)" }}
                >
                  {CONTENT_ITEMS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={item.testid}
                      onClick={() => setContentOpen(false)}
                      className={`block px-4 py-2.5 transition-colors ${dm ? "hover:bg-white/5" : "hover:bg-[#f8f9fa]"}`}
                    >
                      <span className={`block text-xs font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{item.label}</span>
                      <span className={`block text-[11px] mt-0.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{item.desc}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/ask" data-testid="ask-nav-link" className={linkCls(active === "ask")}>
              Ask <Pill text="AI" dm={dm} />
            </Link>
            <Link to="/reports" data-testid="reports-nav-link" className={linkCls(active === "reports")}>
              Reports <Pill text="NEW" dm={dm} />
            </Link>
            <Link to="/insights" data-testid="insights-nav-link" className={linkCls(active === "insights")}>
              Insights <Pill text="BETA" dm={dm} />
            </Link>
          </nav>
        </div>

        {/* Utility zone */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button data-testid="nav-search-btn" onClick={handleSearch} title="Search 3-month story archive" className={utilBtnCls}>
            <Search className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Search</span>
          </button>

          {user && (
            <div className="hidden md:flex items-center gap-3">
              <ArchiveModal darkMode={dm} authHeader={authHeader} />
              <SettingsModal darkMode={dm} authHeader={authHeader} />
            </div>
          )}

          <button
            data-testid="dark-mode-toggle"
            onClick={() => setDmPref((v) => !v)}
            title={dmPref ? "Switch to light theme" : "Switch to dark theme (applies to your feed)"}
            className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-400 hover:bg-[#252e2f] hover:text-slate-100" : "text-[#6b7a7d] hover:bg-[#ecedef] hover:text-[#191c1d]"}`}
          >
            {dmPref ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>

          {user ? (
            <div ref={accountRef} className="relative">
              <button
                data-testid="account-menu-btn"
                onClick={() => setAccountOpen((o) => !o)}
                aria-expanded={accountOpen}
                className="flex items-center gap-1"
              >
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold uppercase ${dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"}`}>
                  {(user.email || "?")[0]}
                </span>
                <ChevronDown className={`w-3 h-3 transition-transform ${accountOpen ? "rotate-180" : ""} ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`} />
              </button>
              {accountOpen && (
                <div
                  data-testid="account-menu"
                  className={`absolute right-0 top-full mt-2 w-64 rounded-xl overflow-hidden z-50 animate-fade-in ${dm ? "bg-[#252e2f] border border-white/5" : "bg-white border border-black/5"}`}
                  style={{ boxShadow: "0 14px 32px rgba(16,40,44,0.14)" }}
                >
                  <div className={`px-4 py-3 border-b ${dm ? "border-white/5" : "border-black/5"}`}>
                    <p className={`text-xs font-semibold truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`} data-testid="account-menu-email">{user.email}</p>
                    <p className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${user.is_pro ? "text-violet-500" : (dm ? "text-slate-500" : "text-[#9aa7a9]")}`}>
                      {user.is_pro ? "PRO" : "Free plan"}{isAdmin ? " · Admin" : ""}
                    </p>
                  </div>
                  <Link to="/subscribe" data-testid="account-menu-subscribe" onClick={() => setAccountOpen(false)}
                    className={`block px-4 py-2.5 text-xs font-medium transition-colors ${dm ? "text-slate-200 hover:bg-white/5" : "text-[#3b494c] hover:bg-[#f8f9fa]"}`}>
                    Subscribe
                  </Link>
                  <Link to="/about" data-testid="account-menu-about" onClick={() => setAccountOpen(false)}
                    className={`block px-4 py-2.5 text-xs font-medium transition-colors ${dm ? "text-slate-200 hover:bg-white/5" : "text-[#3b494c] hover:bg-[#f8f9fa]"}`}>
                    About
                  </Link>
                  {isAdmin && (
                    <button
                      data-testid="admin-mode-toggle"
                      onClick={() => setAdminMode((v) => !v)}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-colors ${
                        adminMode
                          ? (dm ? "text-amber-300 hover:bg-amber-400/10" : "text-[#8a5a06] hover:bg-[#fdf6e8]")
                          : (dm ? "text-slate-200 hover:bg-white/5" : "text-[#3b494c] hover:bg-[#f8f9fa]")
                      }`}
                    >
                      <Shield className={`w-3.5 h-3.5 ${adminMode ? "" : "opacity-60"}`} />
                      Admin mode
                      <span
                        className={`ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                          adminMode
                            ? "text-white"
                            : (dm ? "bg-[#1a2122] text-slate-500" : "bg-[#ecedef] text-[#6b7a7d]")
                        }`}
                        style={adminMode ? { background: "#8a5a06" } : undefined}
                      >
                        {adminMode ? "On" : "Off"}
                      </span>
                    </button>
                  )}
                  {isAdmin && (
                    <Link to="/admin" data-testid="admin-console-link" onClick={() => setAccountOpen(false)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-colors ${dm ? "text-cyan-300 hover:bg-white/5" : "text-[#006874] hover:bg-[#f8f9fa]"}`}>
                      <Shield className="w-3.5 h-3.5" />Admin Console →
                    </Link>
                  )}
                  {push.supported && push.permission !== "denied" && (
                    <button
                      data-testid="push-notif-toggle-btn"
                      onClick={push.subscribed ? push.unsubscribe : push.subscribe}
                      disabled={push.loading}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-50 ${dm ? "text-slate-200 hover:bg-white/5" : "text-[#3b494c] hover:bg-[#f8f9fa]"}`}
                    >
                      {push.subscribed ? <Bell className={`w-3.5 h-3.5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} /> : <BellOff className="w-3.5 h-3.5 opacity-60" />}
                      Brief notifications {push.subscribed ? "on" : "off"}
                    </button>
                  )}
                  <div className={`border-t ${dm ? "border-white/5" : "border-black/5"}`}>
                    <button
                      data-testid="account-menu-signout"
                      onClick={() => { setAccountOpen(false); logout(); }}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-colors ${dm ? "text-red-400 hover:bg-red-400/10" : "text-red-600 hover:bg-red-50"}`}
                    >
                      <LogOut className="w-3.5 h-3.5" />Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/"
              data-testid="public-nav-cta"
              className="category-tag px-4 py-2 rounded-full text-white flex-shrink-0"
              style={{ background: "#006874" }}
            >
              Join free →
            </Link>
          )}
        </div>
      </div>

      {/* Amber Admin-mode ribbon — every page, admins with the mode ON only */}
      {isAdmin && adminMode && <AdminRibbon dm={dm} />}
    </header>

    {/* Mobile bottom tab bar (<768px): Stories · Ask · Reports · Insights · More.
        Rendered OUTSIDE <header> — backdrop-filter creates a containing block
        that would break position:fixed for descendants. */}
    <MobileTabBar active={active} darkMode={dm} />
    </>
  );
}
